import {
  Karla_400Regular,
  Karla_400Regular_Italic,
  Karla_700Bold,
} from '@expo-google-fonts/karla'
import { createTheme } from '@rneui/themed'
import { useFonts } from 'expo-font'
import { Appearance, Platform, Text, TextInput } from 'react-native'

// Icon fonts are only loaded via expo-font on web.
// On native (Android/iOS), react-native-vector-icons auto-links them.
const isWeb = Platform.OS === 'web'

const AntDesign = isWeb
  ? require('@react-native-vector-icons/ant-design/fonts/AntDesign.ttf')
  : null
const Entypo = isWeb
  ? require('@react-native-vector-icons/entypo/fonts/Entypo.ttf')
  : null
const EvilIcons = isWeb
  ? require('@react-native-vector-icons/evil-icons/fonts/EvilIcons.ttf')
  : null
const Feather = isWeb
  ? require('@react-native-vector-icons/feather/fonts/Feather.ttf')
  : null
const FontAwesome = isWeb
  ? require('@react-native-vector-icons/fontawesome/fonts/FontAwesome.ttf')
  : null
const FontAwesome5Brands = isWeb
  ? require('@react-native-vector-icons/fontawesome5/fonts/FontAwesome5_Brands.ttf')
  : null
const FontAwesome5Regular = isWeb
  ? require('@react-native-vector-icons/fontawesome5/fonts/FontAwesome5_Regular.ttf')
  : null
const FontAwesome5Solid = isWeb
  ? require('@react-native-vector-icons/fontawesome5/fonts/FontAwesome5_Solid.ttf')
  : null
const Ionicons = isWeb
  ? require('@react-native-vector-icons/ionicons/fonts/Ionicons.ttf')
  : null
const MaterialCommunityIcons = isWeb
  ? require('@react-native-vector-icons/material-design-icons/fonts/MaterialDesignIcons.ttf')
  : null
const MaterialIcons = isWeb
  ? require('@react-native-vector-icons/material-icons/fonts/MaterialIcons.ttf')
  : null

if ((Text as any).defaultProps == null) {
  ;(Text as any).defaultProps = {}
  ;(TextInput as any).defaultProps = {}
}
;(Text as any).defaultProps.allowFontScaling = false
;(TextInput as any).defaultProps.allowFontScaling = false

export const font = {
  normal: 'Karla_400Regular',
  bold: 'Karla_700Bold',
  italic: 'Karla_400Regular_Italic',
}

// Get the system color scheme for initial theme
const initialColorScheme = Appearance.getColorScheme()

export const appTheme = createTheme({
  mode: initialColorScheme === 'dark' ? 'dark' : 'light',
  darkColors: {
    primary: '#f73961',
    secondary: '#efeff4',
    grey6: '#353b4a',
    blue: '#007aff',
    teal: '#6e969c',
    violet: '#9b98b5',
    grey7: '#121212',
    grey8: '#3B3B4A',
    grey9: '#4E5665',
    grey10: '#242526',
    grey11: '#18191A',
    grey12: '#9B9B9B',
    grey13: '#232830',
    grey14: '#1E1E1E',
    peach: '#FFBBB8',
    red: '#F03962',
    yellow: '#FFE2B3',
  },
  lightColors: {
    primary: '#f73961',
    secondary: '#efeff4',
    grey6: '#f9f9f9',
    blue: '#007aff',
    teal: '#accfd5',
    violet: '#bcbace',
    grey7: '#dcdee0',
    grey8: '#3B3B4A',
    grey9: '#4E5665',
    grey10: '#242526',
    grey11: '#18191A',
    grey12: '#9B9B9B',
    grey13: '#F5F7FA',
    grey14: '#F8F8F8',
    peach: '#FFBBB8',
    red: '#F03962',
    yellow: '#FFE2B3',
  },
  components: {
    Text: {
      allowFontScaling: false,
      style: {
        fontFamily: font.normal,
      },
    },
    Input: {
      allowFontScaling: false,
      renderErrorMessage: false,
      inputContainerStyle: {
        borderBottomWidth: 0,
        height: undefined,
        paddingVertical: 0,
        paddingHorizontal: 0,
      },
      rightIconContainerStyle: {
        paddingLeft: 16,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
        marginVertical: 0,
        marginHorizontal: 0,
        height: undefined,
      },
      leftIconContainerStyle: {
        paddingRight: 16,
        paddingLeft: 0,
        paddingTop: 0,
        paddingBottom: 0,
        marginVertical: 0,
        marginHorizontal: 0,
        height: undefined,
      },
      inputStyle: {
        minHeight: undefined,
        paddingVertical: 0,
        paddingHorizontal: 0,
      },
    },
    Button: {
      titleStyle: {
        fontFamily: font.normal,
      },
      titleProps: { allowFontScaling: false },
    },
    ButtonGroup: {
      textStyle: {
        fontFamily: font.normal,
      },
    },
    CheckBox: {
      fontFamily: font.normal,
    },
    Switch(props, theme) {
      return {
        trackColor: {
          false:
            theme.mode === 'dark' ? theme.colors.grey6 : theme.colors.grey5,
          true: theme.colors.primary,
        },
        ios_backgroundColor:
          theme.mode === 'dark' ? theme.colors.grey6 : theme.colors.grey5,
      }
    },
    ListItemButtonGroup: {
      textStyle: {
        fontFamily: font.normal,
      },
    },
    ListItemCheckBox: {
      fontFamily: font.normal,
    },
    ListItemTitle: {
      allowFontScaling: false,
      style: {
        fontFamily: font.normal,
      },
    },
    ListItemSubtitle: {
      style: {
        fontFamily: font.normal,
      },
    },
  },
})

export function useThemeFonts() {
  return useFonts({
    Karla_400Regular,
    Karla_700Bold,
    Karla_400Regular_Italic,
    // Icon fonts are only needed on web; on native they are auto-linked
    ...(isWeb
      ? {
          FontAwesome,
          'FontAwesome5Free-Regular': FontAwesome5Regular,
          'FontAwesome5Free-Solid': FontAwesome5Solid,
          'FontAwesome5Brands-Regular': FontAwesome5Brands,
          FontAwesome5_Regular: FontAwesome5Regular,
          FontAwesome5_Solid: FontAwesome5Solid,
          FontAwesome5_Brands: FontAwesome5Brands,
          Ionicons,
          'Material Icons': MaterialIcons,
          Feather,
          feather: Feather,
          anticon: AntDesign,
          'ant-design': AntDesign,
          antdesign: AntDesign,
          Entypo,
          EvilIcons,
          evilicon: EvilIcons,
          evilicons: EvilIcons,
          'Material Design Icons': MaterialCommunityIcons,
          MaterialDesignIcons: MaterialCommunityIcons,
          ionicons: Ionicons,
          materialicons: MaterialIcons,
          'material-community': MaterialCommunityIcons,
          'MaterialIcons-Regular': MaterialIcons,
        }
      : {}),
  })
}
